/* eslint-disable no-control-regex */
import moment from 'moment';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { OFFERING_TYPE } from 'src/configs/InputConfig/addDAFormConfig';
import { hourlyRate, regularHourRate, workingDays, workingHours } from 'src/constants/offerLeter';
import globalutil from 'src/util/globalutil';

// Utility: clean text for PDF compatibility
const sanitizePdfText = (text = '') => {
  return String(text)
    .replace(/[\r\n]+/g, '; ') // replace newlines with semicolon+space
    .replace(/[^\x00-\x7F]+/g, '') // remove non-ASCII chars (emojis, etc.)
    .replace(/\s+/g, ' ') // collapse multiple spaces
    .trim();
};

export const generateOfferLetter = async (dsp, daForm, signature, onlyPreview = false) => {
  // Fetch the existing PDF template
  const templatePath = '/templates/DSPOL.pdf';
  const existingPdfBytes = await fetch(templatePath)
    .then((res) => res.arrayBuffer())
    .catch((error) => {
      console.error('Error fetching PDF template:', error);
    });

  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  pdfDoc.setTitle('Offer Letter');

  // Get the pages
  // const pages = pdfDoc.getPages();

  // Variables for replacement text
  const state = globalutil?.states()?.find((s) => s?.id == daForm?.stateId);
  const country = globalutil?.countries()?.find((s) => s?.id == state?.desc);

  const variables = {
    DATE: signature
      ? moment(daForm?.createdAt).format('MM/DD/YYYY')
      : moment().format('MM/DD/YYYY'),
    DSP_NAME: dsp?.name || '',
    DSP_ADDRESS: dsp?.surfaceAddress || '',
    DSP_EMAIL: dsp?.email || '',
    DSP_TRADENAME: dsp?.tradeName || '',
    DSP_CONTACT: dsp?.contact || '',
    // DSP_CONTACT: daForm.primaryContact || '',
    NAME: daForm.firstName + ' ' + daForm.lastName,
    EMAIL: daForm.email,
    CONTACT: daForm.primaryContact,
    POSITION: daForm.positionType,
    ADDRESS: daForm?.address || '',
    WORKING_HOURS: workingHours,
    WORKING_DAYS: workingDays,
    HOURLY_RATE: dsp?.hourlyRate ?? hourlyRate,
    REGULAR_HOURLY_RATE: regularHourRate,
    // CONTACT:
    // 🟢 Newly added
    STATE: state?.name || daForm.stateName || '',
    POSTAL_CODE: daForm.postalCode || '',
    COUNTRY: country?.name || '',
  };
  let imagePath = '';
  const defaultImage = '4DSPLog.jpeg';

  // Determine the image path and validate the extension
  if (dsp?.logo) {
    const extension = dsp.logo.split('.').pop().toLowerCase();
    if (extension === 'png' || extension === 'jpg') {
      imagePath = dsp.logo;
    } else {
      imagePath = defaultImage;
    }
  } else {
    imagePath = defaultImage;
  }

  let imageBytes;
  try {
    // Attempt to fetch the remote image
    const response = await fetch(imagePath);
    if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
    imageBytes = await response.arrayBuffer();
  } catch (error) {
    console.error('Error fetching remote image, using default image:', error.message);

    // Fetch the default image bytes if the remote fetch fails
    const defaultImageResponse = await fetch(defaultImage);
    imageBytes = await defaultImageResponse.arrayBuffer();
    imagePath = defaultImage;
  }

  // Embed the image in the PDF
  let image;
  if (imagePath.endsWith('.png')) {
    image = await pdfDoc.embedPng(imageBytes);
  } else {
    image = await pdfDoc.embedJpg(imageBytes);
  }
  // Load the PDF

  // Embed a built-in font (e.g., Helvetica)
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const color = rgb(0, 0, 0);

  // Get the first page of the PDF
  const page = pdfDoc.getPages()[0];

  // Find the positions of the placeholders and update with dynamic data
  // Example: Replace [name] and [address]
  const { width, height } = page.getSize();

  // Get the width/height of the PNG image scaled down to 50% of its original size

  const allPages = pdfDoc.getPages();

  allPages.forEach((page) => {
    page.drawRectangle({
      x: 0, // X position of the background
      y: height - 80, // Y position of the background
      width: 100, // Width of the background box (adjust as needed)
      height: 50, // Height of the background box (adjust as needed)
      color: rgb(1, 1, 1), // White color
    });
    page.drawImage(image, {
      x: 10,
      y: height - 80,
      width: 80,
      height: 60,
    });
    page.drawRectangle({
      x: 0, // X position of the background
      y: height - 775, // Y position of the background
      width: 260, // Width of the background box (adjust as needed)
      height: 55, // Height of the background box (adjust as needed)
      color: rgb(1, 1, 1), // White color
    });
    const bottomText = `${variables.DSP_CONTACT}\n${variables.DSP_EMAIL}\n${variables.DSP_ADDRESS}`;
    page.drawText(bottomText, {
      x: 10, // X position of the placeholder
      y: height - 735, // Y position of the placeholder
      size: 12, // Text size
      color: rgb(0, 0, 0),
      font: font,
      lineHeight: 15,
    });
  });

  // Replace [name]
  page.drawRectangle({
    x: 80, // X position of the background
    y: height - 98, // Y position of the background
    width: width, // Width of the background box (adjust as needed)
    height: 18, // Height of the background box (adjust as needed)
    color: rgb(1, 1, 1), // White color
  });

  // Replace [name] with dynamic data (date in this case)
  // Draw trade name
  page.drawText(variables.DSP_TRADENAME, {
    x: width / 2.2,
    y: height - 98,
    size: 12,
    color,
    font: boldFont,
  });

  // Background rectangle
  page.drawRectangle({
    x: 10,
    y: height - 230,
    width: width,
    height: 130,
    color: rgb(1, 1, 1),
  });

  // ✅ Address, Email, Contact — now wrapped text
  const fullText = sanitizePdfText(
    `${variables.DSP_ADDRESS}; ${variables.DSP_EMAIL}; ${variables.DSP_CONTACT}`,
  );

  const fontSize = 12;
  const maxWidth = width - 100; // available width for text
  const wrappedLines = [];

  if (font) {
    const words = fullText.split(' ');
    let line = '';

    for (const word of words) {
      const testLine = line ? `${line} ${word}` : word;
      const textWidth = font.widthOfTextAtSize(testLine, fontSize);

      if (textWidth < maxWidth) {
        line = testLine;
      } else {
        wrappedLines.push(line);
        line = word;
      }
    }

    if (line) wrappedLines.push(line);
  }

  let yPosition = height - 120;
  wrappedLines.forEach((line, index) => {
    page.drawText(line, {
      x: 70,
      y: yPosition - index * 15, // move each new line down
      size: fontSize,
      color,
      font,
    });
  });

  // Date rectangle
  // page.drawRectangle({
  //   x: 70,
  //   y: height - 167,
  //   width: 50,
  //   height: 30,
  //   color: rgb(1, 1, 1),
  // });

  // Replace [name] with dynamic data (date in this case)
  page.drawText(variables.DATE, {
    x: 70, // X position of the placeholder
    y: height - 160, // Y position of the placeholder
    size: 12, // Text size
    color,
  });

  //dsp name
  // page.drawRectangle({
  //   x: 70, // X position of the background
  //   y: height - 190, // Y position of the background
  //   width: 50, // Width of the background box (adjust as needed)
  //   height: 25, // Height of the background box (adjust as needed)
  //   color: rgb(1, 1, 1),
  // });
  page.drawText(variables?.NAME || '', {
    x: 70, // X position of the placeholder
    y: height - 185, // Y position of the placeholder
    size: 12, // Text size
    color,
  });
  //dsp address
  // page.drawRectangle({
  //   x: 70, // X position of the background
  //   y: height - 200, // Y position of the background
  //   width: 215, // Width of the background box (adjust as needed)
  //   height: 20, // Height of the background box (adjust as needed)
  //   color: rgb(1, 1, 1),
  // });

  const ADDRESS = variables.ADDRESS;

  const wrappedLinesAddress = [];
  if (font) {
    const words = ADDRESS.split(' ');
    let line = '';

    for (const word of words) {
      const testLine = line ? `${line} ${word}` : word;
      const textWidth = font.widthOfTextAtSize(testLine, fontSize);

      if (textWidth < maxWidth) {
        line = testLine;
      } else {
        wrappedLinesAddress.push(line);
        line = word;
      }
    }

    if (line) wrappedLinesAddress.push(line);
  }

  yPosition = height - 200;
  wrappedLinesAddress.forEach((line, index) => {
    page.drawText(line, {
      x: 70,
      y: yPosition - index * 15, // move each new line down
      size: fontSize,
      color,
      font,
    });
  });

  // page.drawText(variables.ADDRESS, {
  //   x: 70, // X position of the placeholder
  //   y: height - 200, // Y position of the placeholder
  //   size: 12, // Text size
  //   color,
  // });

  // page.drawRectangle({
  //   x: 70, // X position of the background
  //   y: height - 210, // Y position of the background
  //   width: 215, // Width of the background box (adjust as needed)
  //   height: 20, // Height of the background box (adjust as needed)
  //   color: rgb(1, 1, 1), // White color
  // });
  if (variables?.STATE || variables?.COUNTRY) {
    page.drawText(`${variables.STATE}, ${variables.COUNTRY}`, {
      x: 70,
      y: height - 215,
      size: 12,
      color,
    });
  }

  //re
  page.drawRectangle({
    x: 70, // X position of the background
    y: height - 260, // Y position of the background
    width: width - 140, // Width of the background box (adjust as needed)
    height: 20, // Height of the background box (adjust as needed)
    color: rgb(1, 1, 1), // White color
  });

  page.drawText(
    `Re: Conditional Offer of Employment – ${
      OFFERING_TYPE.find((s) => daForm.offerType == s.id)?.name ?? ''
    }`,
    {
      x: width / 4, // X position of the placeholder
      y: height - 255, // Y position of the placeholder
      size: 12, // Text size
      color,
    },
  );
  //dear
  // page.drawRectangle({
  //   x: 70, // X position of the background
  //   y: height - 230, // Y position of the background
  //   width: 215, // Width of the background box (adjust as needed)
  //   height: 20, // Height of the background box (adjust as needed)
  //   color: rgb(1, 1, 1), // White color
  // });
  page.drawText(
    `Dear, ${globalutil?.suffixes().find((s) => daForm.suffix == s.id)?.name ?? ''} ${
      variables.NAME
    }`,
    {
      x: 70, // X position of the placeholder
      y: height - 280, // Y position of the placeholder
      size: 12, // Text size
      color,
    },
  );
  //da name
  page.drawRectangle({
    x: 70, // X position of the background
    y: height - 285, // Y position of the background
    width: 215, // Width of the background box (adjust as needed)
    height: 20, // Height of the background box (adjust as needed)
    color: rgb(1, 1, 1), // White color
  });
  page.drawText(
    `Dear, ${globalutil?.suffixes().find((s) => daForm.suffix == s.id)?.name ?? ''} ${
      variables.NAME
    }`,
    {
      x: 70, // X position of the placeholder
      y: height - 280, // Y position of the placeholder
      size: 12, // Text size
      color,
    },
  );
  page.drawRectangle({
    x: 70, // X position of the background
    y: height - 348, // Y position of the background
    width: width - 140, // Width of the background box (adjust as needed)
    height: 50, // Height of the background box (adjust as needed)
    color: rgb(1, 1, 1), // White color
  });
  const text = `We are pleased to offer you employment with ${variables.DSP_TRADENAME}. This offer of employment is conditioned upon your satisfactory completion of certain requirements, as more fully explained in this letter`;
  page.drawText(text, {
    x: 72, // X position of the placeholder
    y: height - 300, // Y position of the placeholder
    size: 11, // Text size
    color: rgb(0, 0, 0),
    lineHeight: 14,
    font,
    maxWidth: width / 1.3,

    // letterSpacing: 0.5,
  });
  page.drawRectangle({
    x: 70, // X position of the background
    y: height - 535, // Y position of the background
    width: width - 140, // Width of the background box (adjust as needed)
    height: 80, // Height of the background box (adjust as needed)
    color: rgb(1, 1, 1), // White color
  });
  const startDate = moment().format('dddd D, MMMM, YYYY');
  const position = variables.POSITION === 3 ? 'DA,' : 'Driver Helper,';
  const dspName = variables.DSP_NAME;
  const wHours = variables.WORKING_HOURS;
  const wDays = variables.WORKING_DAYS;

  const positionMessage = `We would like you to start work no later than ${startDate}. Your position will be ${position} reporting to ${dspName}. This position is a ${
    OFFERING_TYPE.find((o) => o.id == daForm.offerType)?.name ?? ''
  } position with regularly scheduled hours of ${wHours} working ${wDays} days a week. Of course, actual hours may vary depending on the Company’s business needs. A particular schedule or number of hours is not guaranteed and the number of hours may change from week to week.`;
  page.drawText(positionMessage, {
    x: 70, // X position of the placeholder
    y: height - 470, // Y position of the placeholder
    size: 11, // Text size
    color,
    lineHeight: 15,
    maxWidth: width / 1.3,

    // lineHeight: 1.5,
    // blendMode: 'normal',
  });
  page.drawRectangle({
    x: 70, // X position of the background
    y: height - 680, // Y position of the background
    width: width - 140, // Width of the background box (adjust as needed)
    height: 140, // Height of the background box (adjust as needed)
    color: rgb(1, 1, 1), // White color
  });
  const rateMessage = `This is a non-exempt position. Your hourly rate will be $${
    variables.HOURLY_RATE
  }, subject to appropriate \ntax withholdings and deductions, payable in accordance with the Company’s normal payroll cycle.\nYour overtime rate for hours worked over 40 hours in a workweek, or as otherwise required by \napplicable law, will be a minimum of $${
    variables.HOURLY_RATE * 1.5
  }, unless higher on account of a pay raise or receipt of an \nadditional payment that must be included in the regular rate of pay, subject to appropriate tax \nwithholdings and deductions, payable in accordance with the Company’s normal payroll cycle.\nCurrently, the Company processes payroll on a biweekly basis with paydays on Friday. You will \nbe paid by check, unless you elect a different form of payment, such as direct deposit.`;
  page.drawText(rateMessage, {
    x: 70, // X position of the placeholder
    y: height - 555, // Y position of the placeholder
    size: 11, // Text size
    color,
    lineHeight: 15,
  });
  //start date
  const thirdPage = allPages[2];
  thirdPage.drawText(`${variables.DATE}`, {
    x: 300, // X position of the placeholder
    y: height - 515, // Y position of the placeholder
    size: 11, // Text size
    color,
    font: boldFont,
  });
  thirdPage.drawText(
    `${globalutil?.suffixes().find((s) => daForm.suffix == s.id)?.name ?? ''} ${
      daForm?.firstName
    } ${daForm?.lastName}`,
    {
      x: 72, // X position of the placeholder
      y: height - 558, // Y position of the placeholder
      size: 11, // Text size
      color,
      font: boldFont,
    },
  );

  const eightthPage = allPages[7];
  eightthPage.drawText(`${variables.DATE}`, {
    x: 280, // X position of the placeholder
    y: height - 580, // Y position of the placeholder
    size: 11, // Text size
    color,
    font: boldFont,
  });
  eightthPage.drawText(
    `${globalutil?.suffixes().find((s) => daForm.suffix == s.id)?.name ?? ''} ${
      daForm?.firstName
    } ${daForm?.lastName}`,
    {
      x: 72, // X position of the placeholder
      y: height - 655, // Y position of the placeholder
      size: 11, // Text size
      color,
      font: boldFont,
    },
  );

  const ninthPage = allPages[8];

  // ninthPage.drawText(
  //   `${globalutil?.suffixes().find((s) => daForm.suffix == s.id)?.name ?? ''} ${daForm?.firstName} ${
  //     daForm?.lastName
  //   }`,
  //   {
  //     x: 130, // X position of the placeholder
  //     y: height - 357, // Y position of the placeholder
  //     size: 11, // Text size
  //     color,
  //     font: boldFont,
  //   },
  // );
  ninthPage.drawText(`${variables.DATE}`, {
    x: 135, // X position of the placeholder
    y: height - 498, // Y position of the placeholder
    size: 11, // Text size
    color,
    font: boldFont,
  });
  ninthPage.drawText(
    `${globalutil?.suffixes().find((s) => daForm.suffix == s.id)?.name ?? ''} ${
      daForm?.firstName
    } ${daForm?.lastName}`,
    {
      x: 135, // X position of the placeholder
      y: height - 450, // Y position of the placeholder
      size: 11, // Text size
      color,
      font: boldFont,
    },
  );

  if (signature) {
    let pathStrings = [...signature.matchAll(/d="([^"]*)"/g)].map((match) => match[1]);

    pathStrings.forEach((pathString) => {
      thirdPage.drawSvgPath(pathString, {
        x: 72,
        y: height - 470,
        // color,
        borderColor: color,
        borderWidth: 5,
        scale: 0.3,
      });
      eightthPage.drawSvgPath(pathString, {
        x: 72,
        y: height - 540,
        // color,
        borderColor: color,
        borderWidth: 5,
        scale: 0.3,
      });
      ninthPage.drawSvgPath(pathString, {
        x: 135,
        y: height - 360,
        // color,
        borderColor: color,
        borderWidth: 5,
        scale: 0.3,
      });
    });
  }
  // Save the updated PDF
  const pdfBytes = await pdfDoc.save();

  // Create a Blob for download
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });

  // Create a URL for the Blob and open or download the PDF
  const url = URL.createObjectURL(blob);
  if (!onlyPreview) window.open(url); // Open in new tab
  return url;
};
